export const idlFactory = ({ IDL }) => {
  const AuthError = IDL.Variant({
    'Unauthorized' : IDL.Text,
    'InvalidRole' : IDL.Text,
    'AdminAlreadyAssigned' : IDL.Null,
    'InternalError' : IDL.Text,
  });
  const CollateralType = IDL.Variant({
    'ICP' : IDL.Null,
    'ckBTC' : IDL.Null,
    'ckETH' : IDL.Null,
  });
  const Timestamp = IDL.Int;
  const OracleError = IDL.Variant({
    'AssetNotSupported' : CollateralType,
    'PriceOutOfRange' : IDL.Record({
      'max' : IDL.Nat,
      'min' : IDL.Nat,
      'asset' : CollateralType,
      'price' : IDL.Nat,
    }),
    'InternalError' : IDL.Text,
    'StalePrice' : IDL.Record({
      'asset' : CollateralType,
      'lastUpdated' : Timestamp,
    }),
  });
  const TokenError = IDL.Variant({
    'InvalidAmount' : IDL.Text,
    'InsufficientBalance' : IDL.Record({
      'available' : IDL.Nat,
      'required' : IDL.Nat,
    }),
    'Unauthorized' : IDL.Text,
    'InternalError' : IDL.Text,
  });
  const GovernanceError = IDL.Variant({
    'AlreadyVoted' : IDL.Nat,
    'ProposalNotPassed' : IDL.Nat,
    'ProposalNotFound' : IDL.Nat,
    'VotingPeriodNotElapsed' : IDL.Nat,
    'ProposalNotActive' : IDL.Nat,
    'InvalidProposal' : IDL.Text,
    'InsufficientStake' : IDL.Record({
      'available' : IDL.Nat,
      'required' : IDL.Nat,
    }),
    'TimelockNotElapsed' : IDL.Nat,
    'Unauthorized' : IDL.Text,
    'InternalError' : IDL.Text,
  });
  const VaultError = IDL.Variant({
    'InvalidAmount' : IDL.Text,
    'BelowMinimumRatio' : IDL.Record({
      'minimumE8' : IDL.Nat,
      'currentE8' : IDL.Nat,
    }),
    'InsufficientCollateral' : IDL.Record({
      'provided' : IDL.Nat,
      'required' : IDL.Nat,
    }),
    'VaultNotFound' : IDL.Nat,
    'VaultLiquidatable' : IDL.Null,
    'Unauthorized' : IDL.Text,
    'InternalError' : IDL.Text,
  });
  const StakingError = IDL.Variant({
    'InvalidAmount' : IDL.Text,
    'InsufficientStake' : IDL.Record({
      'available' : IDL.Nat,
      'required' : IDL.Nat,
    }),
    'Unauthorized' : IDL.Text,
    'InternalError' : IDL.Text,
  });
  const ProtocolError = IDL.Variant({
    'Internal' : IDL.Text,
    'Auth' : AuthError,
    'Oracle' : OracleError,
    'Token' : TokenError,
    'Governance' : GovernanceError,
    'Vault' : VaultError,
    'Ledger' : IDL.Text,
    'Staking' : StakingError,
  });
  const ProtocolResult = IDL.Variant({
    'ok' : IDL.Null,
    'err' : ProtocolError,
  });
  const ProtocolResult_1 = IDL.Variant({
    'ok' : IDL.Nat,
    'err' : ProtocolError,
  });
  const ProtocolResult_3 = IDL.Variant({
    'ok' : IDL.Record({ 'softLiqs' : IDL.Nat, 'dutchFills' : IDL.Nat }),
    'err' : ProtocolError,
  });
  const CollateralEntry = IDL.Tuple(CollateralType, IDL.Nat);
  const LiquidationPreview = IDL.Record({
    'maxRepayE8' : IDL.Nat,
    'auctionId' : IDL.Opt(IDL.Nat),
    'debtE8' : IDL.Nat,
    'currentAuctionPriceE8' : IDL.Opt(IDL.Nat),
    'bonusBps' : IDL.Nat,
    'estimatedBonusE8' : IDL.Nat,
    'vaultId' : IDL.Nat,
    'commissionE8' : IDL.Nat,
    'seizedCollateral' : IDL.Vec(CollateralEntry),
    'collateralValueE8' : IDL.Nat,
  });
  const ProtocolResult_2 = IDL.Variant({
    'ok' : LiquidationPreview,
    'err' : ProtocolError,
  });
  const UserRole = IDL.Variant({
    'admin' : IDL.Null,
    'user' : IDL.Null,
    'guest' : IDL.Null,
  });
  const PortfolioSummary = IDL.Record({
    'totalDebtE8' : IDL.Nat,
    'atRiskCount' : IDL.Nat,
    'vaultCount' : IDL.Nat,
    'avgHealthFactorE8' : IDL.Nat,
    'totalCollateralUsdE8' : IDL.Nat,
  });
  const ProposalAction = IDL.Variant({
    'SetLiquidationThreshold' : IDL.Record({ 'thresholdE8' : IDL.Nat }),
    'SetStabilityFee' : IDL.Record({
      'feeBps' : IDL.Nat,
      'collateralType' : CollateralType,
    }),
    'TextOnly' : IDL.Null,
    'SetSurplusWaterfallSplit' : IDL.Record({
      'opsBps' : IDL.Nat,
      'dsrBps' : IDL.Nat,
      'mmkBuyBps' : IDL.Nat,
    }),
    'SetDebtCeiling' : IDL.Record({
      'ceiling' : IDL.Nat,
      'collateralType' : CollateralType,
    }),
    'DistributeSurplus' : IDL.Record({
      'description' : IDL.Text,
      'maxAmountE8' : IDL.Nat,
    }),
    'SetSavingsRate' : IDL.Record({ 'rateBps' : IDL.Nat }),
    'SetMinCollateralRatio' : IDL.Record({ 'ratioE8' : IDL.Nat }),
    'SetOracleMaxAge' : IDL.Record({ 'hours' : IDL.Nat }),
    'SetAuctionDurationHours' : IDL.Record({ 'hours' : IDL.Nat }),
    'SetSurplusBuffer' : IDL.Record({ 'targetBufferE8' : IDL.Nat }),
    'SetPause' : IDL.Record({
      'globalPaused' : IDL.Bool,
      'liquidatePaused' : IDL.Bool,
      'mintPaused' : IDL.Bool,
    }),
    'CoverBadDebt' : IDL.Record({
      'description' : IDL.Text,
      'maxMmkMint' : IDL.Nat,
    }),
    'SetTreasurySeizeThreshold' : IDL.Record({ 'thresholdE8' : IDL.Nat }),
    'SetLiquidationBonus' : IDL.Record({ 'bonusBps' : IDL.Nat }),
    'SetGlobalDebtCeiling' : IDL.Record({ 'ceiling' : IDL.Nat }),
    'SetUsdmIcpRedeemParams' : IDL.Record({
      'feeBps' : IDL.Nat,
      'enabled' : IDL.Bool,
    }),
    'SettleBadDebtFromSurplus' : IDL.Record({ 'description' : IDL.Text }),
  });
  const TreasuryFlowDirection = IDL.Variant({
    'In' : IDL.Null,
    'Out' : IDL.Null,
  });
  const TreasuryFlowKind = IDL.Variant({
    'PsmCkUsdcOut' : IDL.Null,
    'SavingsDeposit' : IDL.Null,
    'LiquidationProceeds' : IDL.Null,
    'BadDebtSettlement' : IDL.Null,
    'PsmCkUsdcIn' : IDL.Null,
    'ParamChange' : IDL.Null,
    'TreasurySeizure' : IDL.Null,
    'StabilityFee' : IDL.Null,
    'BadDebtRecord' : IDL.Null,
    'PsmFee' : IDL.Null,
    'DsrReserve' : IDL.Null,
    'OpsReserve' : IDL.Null,
    'CollateralRelease' : IDL.Null,
    'PauseChange' : IDL.Null,
    'UsdmIcpRedeem' : IDL.Null,
    'LiquidityDesk' : IDL.Null,
    'UsdmBurn' : IDL.Null,
    'UsdmMint' : IDL.Null,
    'SurplusDebit' : IDL.Null,
    'AgentCommission' : IDL.Null,
    'SavingsWithdraw' : IDL.Null,
    'GovernanceTransfer' : IDL.Null,
    'CycleTopUp' : IDL.Null,
    'Other' : IDL.Null,
    'LiquidationPayout' : IDL.Null,
    'IcpToCycles' : IDL.Null,
    'MmkSale' : IDL.Null,
    'MmkTreasuryBuy' : IDL.Null,
    'CollateralLock' : IDL.Null,
  });
  const TreasuryFlowEvent = IDL.Record({
    'id' : IDL.Nat,
    'direction' : TreasuryFlowDirection,
    'token' : IDL.Text,
    'fromAccount' : IDL.Text,
    'initiator' : IDL.Text,
    'kind' : TreasuryFlowKind,
    'amountE8' : IDL.Nat,
    'detail' : IDL.Text,
    'vaultId' : IDL.Opt(IDL.Nat),
    'timestamp' : Timestamp,
    'toAccount' : IDL.Text,
  });
  const Alert = IDL.Record({
    'kind' : IDL.Text,
    'tsNs' : IDL.Int,
    'amountE8' : IDL.Nat,
    'detail' : IDL.Text,
    'vaultId' : IDL.Nat,
  });
  const AnalyticsSnapshot = IDL.Record({
    'tvlE8' : IDL.Nat,
    'totalDebtE8' : IDL.Nat,
    'timestamp' : Timestamp,
    'activeVaults' : IDL.Nat,
    'savingsDepositedE8' : IDL.Nat,
    'totalRepaidE8' : IDL.Nat,
  });
  const Paginated_2 = IDL.Record({
    'total' : IDL.Nat,
    'hasMore' : IDL.Bool,
    'offset' : IDL.Nat,
    'limit' : IDL.Nat,
    'items' : IDL.Vec(AnalyticsSnapshot),
  });
  const EventKind = IDL.Variant({
    'SeizureShortfall' : IDL.Null,
    'AuctionShortfall' : IDL.Null,
    'MmkCover' : IDL.Null,
    'Settlement' : IDL.Null,
  });
  const BadDebtEvent = IDL.Record({
    'id' : IDL.Nat,
    'badDebtE8' : IDL.Nat,
    'kind' : EventKind,
    'detail' : IDL.Text,
    'debtOwedE8' : IDL.Nat,
    'vaultId' : IDL.Opt(IDL.Nat),
    'timestamp' : IDL.Int,
    'proceedsE8' : IDL.Nat,
  });
  const UserProfile = IDL.Record({ 'name' : IDL.Text });
  const CollateralParams = IDL.Record({
    'liquidationThresholdE8' : IDL.Nat,
    'minCollateralRatioE8' : IDL.Nat,
    'stabilityFeeBps' : IDL.Nat,
    'collateralType' : CollateralType,
    'debtCeiling' : IDL.Nat,
  });
  const GasFundingStatus = IDL.Record({
    'treasuryMmkE8' : IDL.Nat,
    'gasFloorIcpE8' : IDL.Nat,
    'feeBps' : IDL.Nat,
    'opsIcpE8' : IDL.Nat,
    'open' : IDL.Bool,
    'gasTargetIcpE8' : IDL.Nat,
    'discountBps' : IDL.Nat,
    'spotUsdmPerMmkE8' : IDL.Nat,
  });
  const GasSupporter = IDL.Record({
    'id' : IDL.Nat,
    'messageAt' : IDL.Opt(IDL.Int),
    'amountE8' : IDL.Nat,
    'message' : IDL.Text,
    'donor' : IDL.Principal,
    'donatedAt' : IDL.Int,
  });
  const ProposalStatus = IDL.Variant({
    'active' : IDL.Null,
    'rejected' : IDL.Null,
    'executed' : IDL.Null,
    'passed' : IDL.Null,
  });
  const PublicGovernanceProposal = IDL.Record({
    'id' : IDL.Nat,
    'status' : ProposalStatus,
    'title' : IDL.Text,
    'created' : Timestamp,
    'creator' : IDL.Principal,
    'action' : ProposalAction,
    'votesAgainst' : IDL.Nat,
    'votesFor' : IDL.Nat,
    'passedAt' : IDL.Opt(Timestamp),
    'description' : IDL.Text,
  });
  const Paginated_1 = IDL.Record({
    'total' : IDL.Nat,
    'hasMore' : IDL.Bool,
    'offset' : IDL.Nat,
    'limit' : IDL.Nat,
    'items' : IDL.Vec(PublicGovernanceProposal),
  });
  const Vault = IDL.Record({
    'id' : IDL.Nat,
    'created' : Timestamp,
    'owner' : IDL.Principal,
    'debt' : IDL.Nat,
    'lastUpdated' : Timestamp,
    'collateral' : IDL.Vec(CollateralEntry),
  });
  const LiquidationAuction = IDL.Record({
    'id' : IDL.Nat,
    'startTime' : Timestamp,
    'cancelled' : IDL.Bool,
    'endTime' : Timestamp,
    'minPriceE8' : IDL.Nat,
    'collateral' : IDL.Vec(CollateralEntry),
    'debtE8' : IDL.Nat,
    'filled' : IDL.Bool,
    'vaultId' : IDL.Nat,
    'startPriceE8' : IDL.Nat,
  });
  const LiquidationOpportunity = IDL.Record({
    'softBandCount' : IDL.Nat,
    'hasWork' : IDL.Bool,
    'dutchAuctionCount' : IDL.Nat,
    'asOfNs' : IDL.Int,
    'liquidatableDebtE8' : IDL.Nat,
  });
  const MmkSwapParams = IDL.Record({
    'capMmkE8' : IDL.Nat,
    'enabled' : IDL.Bool,
    'totalSoldMmkE8' : IDL.Nat,
    'mmkPerUsdmE8' : IDL.Nat,
  });
  const PriceFeed = IDL.Record({
    'providerCount' : IDL.Opt(IDL.Nat),
    'asset' : CollateralType,
    'source' : IDL.Opt(IDL.Text),
    'lastUpdated' : Timestamp,
    'pendingPrice' : IDL.Opt(IDL.Nat),
    'price' : IDL.Nat,
    'pendingActivatesAt' : IDL.Opt(Timestamp),
  });
  const ProtocolAccounting = IDL.Record({
    'vaultTvlUsdE8' : IDL.Nat,
    'surplusUsdmE8' : IDL.Nat,
    'asOf' : Timestamp,
    'demoFaucetEnabled' : IDL.Bool,
    'totalDebtUsdmE8' : IDL.Nat,
    'oracleStale' : IDL.Bool,
    'usdmLedgerWired' : IDL.Bool,
    'savingsDepositedUsdmE8' : IDL.Nat,
    'usdmSupplyE8' : IDL.Opt(IDL.Nat),
  });
  const ProtocolParams = IDL.Record({
    'liquidationThresholdE8' : IDL.Nat,
    'minCollateralRatioE8' : IDL.Nat,
    'liquidationBonusBps' : IDL.Nat,
    'globalPaused' : IDL.Bool,
    'totalDebt' : IDL.Nat,
    'savingsRateBps' : IDL.Nat,
    'executionDelayHours' : IDL.Nat,
    'globalDebtCeiling' : IDL.Nat,
    'liquidatePaused' : IDL.Bool,
    'mintPaused' : IDL.Bool,
  });
  const VaultStatus = IDL.Variant({
    'Active' : IDL.Null,
    'Finished' : IDL.Null,
    'Liquidated' : IDL.Null,
    'AtRisk' : IDL.Null,
  });
  const PublicVaultView = IDL.Record({
    'id' : IDL.Nat,
    'ownerLabel' : IDL.Text,
    'status' : VaultStatus,
    'created' : Timestamp,
    'owner' : IDL.Text,
    'ltvBps' : IDL.Nat,
    'lastUpdated' : Timestamp,
    'collateral' : IDL.Vec(CollateralEntry),
    'debtE8' : IDL.Nat,
    'healthFactorE8' : IDL.Nat,
    'collateralValueE8' : IDL.Nat,
  });
  const PublicVaultSort = IDL.Variant({
    'ltvDesc' : IDL.Null,
    'healthAsc' : IDL.Null,
    'ltvAsc' : IDL.Null,
    'newest' : IDL.Null,
    'debtDesc' : IDL.Null,
  });
  const PublicVaultPage = IDL.Record({
    'total' : IDL.Nat,
    'vaults' : IDL.Vec(PublicVaultView),
  });
  const StabilitySwapStatus = IDL.Record({
    'psmCapE8' : IDL.Nat,
    'psmInventoryE8' : IDL.Nat,
    'ckusdcLedger' : IDL.Opt(IDL.Principal),
    'feeBps' : IDL.Nat,
    'enabled' : IDL.Bool,
    'ledgerWired' : IDL.Bool,
  });
  const SurplusBuckets = IDL.Record({
    'minSurplusHealthE8' : IDL.Nat,
    'treasuryMmkE8' : IDL.Nat,
    'minOpsUsdmHealthE8' : IDL.Nat,
    'status' : IDL.Text,
    'redeemEnabled' : IDL.Bool,
    'psmFeeBps' : IDL.Nat,
    'psmCapE8' : IDL.Nat,
    'redeemFeeBps' : IDL.Nat,
    'masterE8' : IDL.Nat,
    'opsIcpE8' : IDL.Nat,
    'dsrReserveE8' : IDL.Nat,
    'psmCkUsdcE8' : IDL.Nat,
    'psmEnabled' : IDL.Bool,
    'waterfallOpsBps' : IDL.Nat,
    'opsUsdmE8' : IDL.Nat,
    'targetBufferE8' : IDL.Nat,
    'distributionPreviewE8' : IDL.Nat,
    'waterfallDsrBps' : IDL.Nat,
    'bufferRatioBps' : IDL.Nat,
    'waterfallMmkBps' : IDL.Nat,
  });
  const Token = IDL.Variant({ 'MMK' : IDL.Null, 'USDM' : IDL.Null });
  const TokenomicsStatus = IDL.Record({
    'gas' : GasFundingStatus,
    'treasuryMmkE8' : IDL.Nat,
    'capMmkE8' : IDL.Nat,
    'extensionPhase' : IDL.Bool,
    'ownershipCapMmkE8' : IDL.Nat,
    'remainingMintE8' : IDL.Nat,
    'soldMmkE8' : IDL.Nat,
    'spotUsdmPerMmkE8' : IDL.Nat,
    'phaseB' : IDL.Bool,
    'multipleBps' : IDL.Nat,
    'extensionThresholdE8' : IDL.Nat,
  });
  const VaultCollateralBucket = IDL.Record({
    'collateralType' : CollateralType,
    'debtE8' : IDL.Nat,
    'liquidatableCount' : IDL.Nat,
    'liquidatableCollUsdE8' : IDL.Nat,
    'lockedE8' : IDL.Nat,
    'liquidatableDebtE8' : IDL.Nat,
    'lockedUsdE8' : IDL.Nat,
  });
  const TreasuryOverview = IDL.Record({
    'vaultLockedByAsset' : IDL.Vec(VaultCollateralBucket),
    'vaultTvlUsdE8' : IDL.Nat,
    'surplusUsdmE8' : IDL.Nat,
    'asOf' : Timestamp,
    'totalDebtUsdmE8' : IDL.Nat,
    'collateralizationRatioE8' : IDL.Nat,
    'oracleStale' : IDL.Bool,
    'liquidatableCollUsdE8' : IDL.Nat,
    'liquidatableDebtE8' : IDL.Nat,
    'savingsDepositedUsdmE8' : IDL.Nat,
    'liquidatableVaultCount' : IDL.Nat,
    'usdmSupplyE8' : IDL.Opt(IDL.Nat),
  });
  const EnrichedVault = IDL.Record({
    'status' : VaultStatus,
    'vault' : Vault,
    'ltvBps' : IDL.Nat,
    'healthFactorE8' : IDL.Nat,
    'recommendation' : IDL.Opt(IDL.Text),
    'collateralValueE8' : IDL.Nat,
  });
  const Paginated = IDL.Record({
    'total' : IDL.Nat,
    'hasMore' : IDL.Bool,
    'offset' : IDL.Nat,
    'limit' : IDL.Nat,
    'items' : IDL.Vec(EnrichedVault),
  });
  const InitArgs = IDL.Record({ 'admin' : IDL.Opt(IDL.Principal) });
  const StabilitySwapDirection = IDL.Variant({
    'UsdmToCkUsdc' : IDL.Null,
    'CkUsdcToUsdm' : IDL.Null,
  });
  const StabilitySwapQuote = IDL.Record({
    'direction' : StabilitySwapDirection,
    'psmCapE8' : IDL.Nat,
    'psmInventoryE8' : IDL.Nat,
    'feeBps' : IDL.Nat,
    'outE8' : IDL.Nat,
    'inE8' : IDL.Nat,
    'enabled' : IDL.Bool,
    'feeE8' : IDL.Nat,
    'ledgerWired' : IDL.Bool,
  });
  return IDL.Service({
    '_initializeAccessControl' : IDL.Func([], [], []),
    'adminReleaseLiquidationLock' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'adminRetryBurnHeldUsdm' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'adminTriggerAuctionScan' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'adminTriggerBadDebtSettlement' : IDL.Func([], [ProtocolResult_1], []),
    'adminTriggerCycleStation' : IDL.Func([], [ProtocolResult_1], []),
    'adminTriggerProtocolBackstop' : IDL.Func([], [ProtocolResult_3], []),
    'adminTriggerWaterfall' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'agentCreateVault' : IDL.Func([IDL.Principal], [ProtocolResult_1], []),
    'agentDepositCollateral' : IDL.Func(
        [IDL.Principal, IDL.Nat, CollateralType, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'agentFillLiquidationAuction' : IDL.Func(
        [IDL.Principal, IDL.Nat],
        [ProtocolResult_2],
        [],
      ),
    'agentLiquidateVault' : IDL.Func(
        [IDL.Principal, IDL.Nat, IDL.Nat],
        [ProtocolResult_2],
        [],
      ),
    'agentLiquidateVaultAmount' : IDL.Func(
        [IDL.Principal, IDL.Nat, IDL.Nat, IDL.Nat],
        [ProtocolResult_2],
        [],
      ),
    'agentMintUSDM' : IDL.Func(
        [IDL.Principal, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'agentRepayUSDM' : IDL.Func(
        [IDL.Principal, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'agentSwapAssetForUsdm' : IDL.Func(
        [IDL.Principal, CollateralType, IDL.Nat],
        [ProtocolResult_1],
        [],
      ),
    'agentSwapUsdmForAsset' : IDL.Func(
        [IDL.Principal, CollateralType, IDL.Nat],
        [ProtocolResult_1],
        [],
      ),
    'agentWithdrawCollateral' : IDL.Func(
        [IDL.Principal, IDL.Nat, CollateralType, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'assignCallerUserRole' : IDL.Func([IDL.Principal, UserRole], [], []),
    'buyMmkWithIcp' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'buyMmkWithUsdm' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'calculatePortfolioSummary' : IDL.Func([], [PortfolioSummary], ['query']),
    'closeEmptyVault' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'createProposal' : IDL.Func(
        [IDL.Text, IDL.Text, ProposalAction],
        [ProtocolResult_1],
        [],
      ),
    'createVault' : IDL.Func([], [ProtocolResult_1], []),
    'creditOpsIcp' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'creditSurplusFromBook' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'demoMintIcrc' : IDL.Func(
        [IDL.Variant({ 'MMK' : IDL.Null }), IDL.Principal, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'demoSetPriceFeedImmediate' : IDL.Func(
        [CollateralType, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'depositCollateral' : IDL.Func(
        [IDL.Nat, CollateralType, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'depositPsmCkUsdc' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'depositSavings' : IDL.Func([IDL.Float64], [ProtocolResult], []),
    'distributeSurplus' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'donateGasIcp' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'drainTreasuryEvents' : IDL.Func(
        [IDL.Nat],
        [IDL.Vec(TreasuryFlowEvent)],
        [],
      ),
    'ensureAgentUser' : IDL.Func([IDL.Principal], [ProtocolResult], []),
    'executeProposal' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'fillLiquidationAuction' : IDL.Func([IDL.Nat], [ProtocolResult_2], []),
    'getAccountingAlerts' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [IDL.Record({ 'total' : IDL.Nat, 'entries' : IDL.Vec(Alert) })],
        ['query'],
      ),
    'getActiveVaultCount' : IDL.Func([], [IDL.Nat], ['query']),
    'getAdminPricePolicy' : IDL.Func(
        [],
        [
          IDL.Record({
            'maxDeviationBps' : IDL.Nat,
            'cooldownSecs' : IDL.Nat,
            'enabled' : IDL.Bool,
          }),
        ],
        ['query'],
      ),
    'getAgentGateway' : IDL.Func([], [IDL.Opt(IDL.Principal)], ['query']),
    'getAnalyticsHistory' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [Paginated_2],
        ['query'],
      ),
    'getAnalyticsSummary' : IDL.Func(
        [],
        [
          IDL.Record({
            'tvlE8' : IDL.Nat,
            'totalDebtE8' : IDL.Nat,
            'atRiskCount' : IDL.Nat,
            'liquidatableCount' : IDL.Nat,
            'activeVaults' : IDL.Nat,
            'savingsDepositedE8' : IDL.Nat,
            'totalRepaidE8' : IDL.Nat,
          }),
        ],
        [],
      ),
    'getAnalyticsSummaryQuery' : IDL.Func(
        [],
        [
          IDL.Record({
            'tvlE8' : IDL.Nat,
            'totalDebtE8' : IDL.Nat,
            'atRiskCount' : IDL.Nat,
            'liquidatableCount' : IDL.Nat,
            'activeVaults' : IDL.Nat,
            'savingsDepositedE8' : IDL.Nat,
            'totalRepaidE8' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'getAuctionDurationNanos' : IDL.Func([], [IDL.Int], ['query']),
    'getAutomationHealth' : IDL.Func(
        [],
        [
          IDL.Record({
            'opsUsdmBoostEnabled' : IDL.Bool,
            'oracleFresh' : IDL.Bool,
            'cyclesSoftBackstopFloor' : IDL.Nat,
            'lastCycleStationDonated' : IDL.Nat,
            'lastDutchFills' : IDL.Nat,
            'lastCycleStationNs' : IDL.Int,
            'lastFundGasE8' : IDL.Nat,
            'skippedNoOps' : IDL.Nat,
            'phaseBPendingUsdmE8' : IDL.Nat,
            'lastWaterfallDebitedE8' : IDL.Nat,
            'badDebtTimerArmed' : IDL.Bool,
            'phaseBActive' : IDL.Bool,
            'lastOracleSyncNs' : IDL.Int,
            'cycleStationEnabled' : IDL.Bool,
            'softBackstopCyclesOk' : IDL.Bool,
            'waterfallTimerArmed' : IDL.Bool,
            'cyclesTrapFloor' : IDL.Nat,
            'waterfallIntervalSecs' : IDL.Nat,
            'cycleStationTimerArmed' : IDL.Bool,
            'lastPhaseBPaidE8' : IDL.Nat,
            'tiltEnabled' : IDL.Bool,
            'backstopTimerArmed' : IDL.Bool,
            'opsUsdmE8' : IDL.Nat,
            'oracleAutoSyncEnabled' : IDL.Bool,
            'liquidationsAllowed' : IDL.Bool,
            'lastBackstopScanNs' : IDL.Int,
            'analyticsTimerArmed' : IDL.Bool,
            'phaseBEnabled' : IDL.Bool,
            'waterfallAutoEnabled' : IDL.Bool,
            'oracleSyncIntervalSecs' : IDL.Nat,
            'pendingGasUsdmE8' : IDL.Nat,
            'cyclesBalance' : IDL.Nat,
            'lastSoftLiqs' : IDL.Nat,
            'oracleSyncTimerArmed' : IDL.Bool,
            'liqTimerArmed' : IDL.Bool,
            'lastWaterfallNs' : IDL.Int,
          }),
        ],
        ['query'],
      ),
    'getBadDebtEvents' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [
          IDL.Record({
            'total' : IDL.Nat,
            'offset' : IDL.Nat,
            'limit' : IDL.Nat,
            'events' : IDL.Vec(BadDebtEvent),
          }),
        ],
        ['query'],
      ),
    'getBadDebtSummary' : IDL.Func(
        [],
        [
          IDL.Record({
            'openBadDebtE8' : IDL.Nat,
            'reserveE8' : IDL.Nat,
            'eventCount' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'getCallerUserProfile' : IDL.Func([], [IDL.Opt(UserProfile)], ['query']),
    'getCallerUserRole' : IDL.Func([], [UserRole], ['query']),
    'getCertifiedAnalyticsSummary' : IDL.Func(
        [],
        [
          IDL.Record({
            'certificate' : IDL.Vec(IDL.Nat8),
            'summary' : IDL.Record({
              'tvlE8' : IDL.Nat,
              'totalDebtE8' : IDL.Nat,
              'atRiskCount' : IDL.Nat,
              'liquidatableCount' : IDL.Nat,
              'activeVaults' : IDL.Nat,
              'savingsDepositedE8' : IDL.Nat,
              'totalRepaidE8' : IDL.Nat,
            }),
          }),
        ],
        ['query'],
      ),
    'getCollateralParamsList' : IDL.Func(
        [],
        [IDL.Vec(CollateralParams)],
        ['query'],
      ),
    'getCycleStationStatus' : IDL.Func(
        [],
        [
          IDL.Record({
            'gasFloorIcpE8' : IDL.Nat,
            'lastDonatedCycles' : IDL.Nat,
            'opsIcpE8' : IDL.Nat,
            'oracle' : IDL.Opt(IDL.Principal),
            'assets' : IDL.Opt(IDL.Principal),
            'lastNs' : IDL.Int,
            'enabled' : IDL.Bool,
            'intervalSecs' : IDL.Nat,
            'gateway' : IDL.Opt(IDL.Principal),
            'cmcEnabled' : IDL.Bool,
            'lastIcpSpentE8' : IDL.Nat,
            'softFloorCycles' : IDL.Nat,
            'maxIcpPerTickE8' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'getDemoFaucet' : IDL.Func([], [IDL.Opt(IDL.Principal)], ['query']),
    'getEffectivePrice' : IDL.Func([CollateralType], [IDL.Nat], ['query']),
    'getGasFundingStatus' : IDL.Func([], [GasFundingStatus], ['query']),
    'getGasSupporters' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [IDL.Record({ 'total' : IDL.Nat, 'events' : IDL.Vec(GasSupporter) })],
        ['query'],
      ),
    'getGovernanceParams' : IDL.Func(
        [],
        [
          IDL.Record({
            'minStakeMmkE8' : IDL.Nat,
            'votingPeriodNanos' : IDL.Int,
            'paramProposalQuorum' : IDL.Nat,
            'executionDelayNanos' : IDL.Int,
          }),
        ],
        ['query'],
      ),
    'getGovernanceProposals' : IDL.Func(
        [],
        [IDL.Vec(PublicGovernanceProposal)],
        ['query'],
      ),
    'getGovernanceProposalsPaginated' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [Paginated_1],
        ['query'],
      ),
    'getLedgerCanisters' : IDL.Func(
        [],
        [
          IDL.Record({
            'mmk' : IDL.Opt(IDL.Principal),
            'oracle' : IDL.Opt(IDL.Principal),
            'usdm' : IDL.Opt(IDL.Principal),
          }),
        ],
        ['query'],
      ),
    'getLiquidatableVaults' : IDL.Func([], [IDL.Vec(Vault)], ['query']),
    'getLiquidationAuctions' : IDL.Func(
        [],
        [IDL.Vec(LiquidationAuction)],
        ['query'],
      ),
    'getLiquidationOpportunity' : IDL.Func(
        [],
        [LiquidationOpportunity],
        ['query'],
      ),
    'getLiquidatorUsdmEscrowAccount' : IDL.Func(
        [IDL.Principal],
        [
          IDL.Record({
            'owner' : IDL.Principal,
            'subaccount' : IDL.Vec(IDL.Nat8),
          }),
        ],
        ['query'],
      ),
    'getLiquidityTreasury' : IDL.Func(
        [],
        [
          IDL.Record({
            'minSurplusHealthE8' : IDL.Nat,
            'treasuryMmkE8' : IDL.Nat,
            'minOpsUsdmHealthE8' : IDL.Nat,
            'deskEnabled' : IDL.Bool,
            'ckBtcE8' : IDL.Nat,
            'opsIcpE8' : IDL.Nat,
            'note' : IDL.Text,
            'psmCkUsdcE8' : IDL.Nat,
            'gasTargetIcpE8' : IDL.Nat,
            'opsUsdmE8' : IDL.Nat,
            'extensionArmed' : IDL.Bool,
            'surplusMasterE8' : IDL.Nat,
            'deskFeeBps' : IDL.Nat,
            'ckEthE8' : IDL.Nat,
            'gasFundingOpen' : IDL.Bool,
          }),
        ],
        ['query'],
      ),
    'getMmkBookCanister' : IDL.Func([], [IDL.Opt(IDL.Principal)], ['query']),
    'getMmkLedgerBalance' : IDL.Func([], [IDL.Float64], []),
    'getMmkSwapParams' : IDL.Func([], [MmkSwapParams], ['query']),
    'getPauseFlags' : IDL.Func(
        [],
        [
          IDL.Record({
            'globalPaused' : IDL.Bool,
            'liquidatePaused' : IDL.Bool,
            'mintPaused' : IDL.Bool,
          }),
        ],
        ['query'],
      ),
    'getPhaseBStatus' : IDL.Func(
        [],
        [
          IDL.Record({
            'pendingUsdmE8' : IDL.Nat,
            'cursor' : IDL.Nat,
            'lastNs' : IDL.Int,
            'maxRecipientsPerTick' : IDL.Nat,
            'lastPaidE8' : IDL.Nat,
            'phaseBEnabled' : IDL.Bool,
            'phaseB' : IDL.Bool,
            'lastRecipients' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'getPriceFeeds' : IDL.Func([], [IDL.Vec(PriceFeed)], ['query']),
    'getProductionHardening' : IDL.Func([], [IDL.Bool], ['query']),
    'getProtocolAccounting' : IDL.Func([], [ProtocolAccounting], []),
    'getProtocolBackstopStatus' : IDL.Func(
        [],
        [
          IDL.Record({
            'lastScanNs' : IDL.Int,
            'dutchGraceNanos' : IDL.Int,
            'lastDutchFills' : IDL.Nat,
            'skippedNoOps' : IDL.Nat,
            'intervalSecs' : IDL.Nat,
            'opsUsdmE8' : IDL.Nat,
            'seizeThresholdE8' : IDL.Nat,
            'lastSoftLiqs' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'getProtocolParams' : IDL.Func([], [ProtocolParams], ['query']),
    'getPublicVault' : IDL.Func(
        [IDL.Nat],
        [IDL.Opt(PublicVaultView)],
        ['query'],
      ),
    'getPublicVaults' : IDL.Func(
        [IDL.Nat, IDL.Nat, PublicVaultSort],
        [PublicVaultPage],
        ['query'],
      ),
    'getRecommendations' : IDL.Func([IDL.Nat], [IDL.Opt(IDL.Text)], ['query']),
    'getSavingsBalance' : IDL.Func([], [IDL.Float64], ['query']),
    'getStabilitySwapStatus' : IDL.Func([], [StabilitySwapStatus], ['query']),
    'getStakingBalance' : IDL.Func([], [IDL.Float64], ['query']),
    'getSurplusBuckets' : IDL.Func([], [SurplusBuckets], ['query']),
    'getSurplusUsdmE8' : IDL.Func([], [IDL.Nat], ['query']),
    'getTVL' : IDL.Func([], [IDL.Nat], ['query']),
    'getTeamAllocRemaining' : IDL.Func([], [IDL.Nat], ['query']),
    'getTokenBalance' : IDL.Func([Token], [IDL.Float64], ['query']),
    'getTokenomicsStatus' : IDL.Func([], [TokenomicsStatus], ['query']),
    'getTotalBorrowVolume' : IDL.Func([], [IDL.Nat], ['query']),
    'getTreasuryFlows' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [
          IDL.Record({
            'total' : IDL.Nat,
            'offset' : IDL.Nat,
            'limit' : IDL.Nat,
            'events' : IDL.Vec(TreasuryFlowEvent),
          }),
        ],
        ['query'],
      ),
    'getTreasuryFlowsForPrincipal' : IDL.Func(
        [IDL.Principal, IDL.Nat, IDL.Nat],
        [
          IDL.Record({
            'total' : IDL.Nat,
            'offset' : IDL.Nat,
            'limit' : IDL.Nat,
            'events' : IDL.Vec(TreasuryFlowEvent),
          }),
        ],
        ['query'],
      ),
    'getTreasuryIndex' : IDL.Func([], [IDL.Opt(IDL.Principal)], ['query']),
    'getTreasuryOverview' : IDL.Func([], [TreasuryOverview], ['query']),
    'getTreasurySeizeThresholdE8' : IDL.Func([], [IDL.Nat], ['query']),
    'getTreasuryStatus' : IDL.Func(
        [],
        [
          IDL.Record({
            'status' : IDL.Text,
            'emergencyThresholdE8' : IDL.Nat,
            'balanceE8' : IDL.Nat,
            'targetBufferE8' : IDL.Nat,
            'distributionPreviewE8' : IDL.Nat,
            'bufferRatioBps' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'getUsdmLedgerBalance' : IDL.Func([], [IDL.Float64], []),
    'getUserVaults' : IDL.Func([], [IDL.Vec(EnrichedVault)], ['query']),
    'getUserVaultsPaginated' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [Paginated],
        ['query'],
      ),
    'getVaults' : IDL.Func([], [IDL.Vec(Vault)], ['query']),
    'getVotingPower' : IDL.Func(
        [],
        [
          IDL.Record({
            'unlockedE8' : IDL.Nat,
            'stakedE8' : IDL.Nat,
            'ownershipCapE8' : IDL.Nat,
            'voteWeightE8' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'health' : IDL.Func(
        [],
        [
          IDL.Record({
            'status' : IDL.Text,
            'surplusUsdmE8' : IDL.Nat,
            'savingsDeposited' : IDL.Nat,
            'vaultCount' : IDL.Nat,
            'oracleStale' : IDL.Bool,
            'proposalCount' : IDL.Nat,
            'liquidatePaused' : IDL.Bool,
            'mintPaused' : IDL.Bool,
          }),
        ],
        ['query'],
      ),
    'init' : IDL.Func([IDL.Opt(InitArgs)], [], ['oneway']),
    'initializeAccessControl' : IDL.Func([], [], []),
    'installBootstrap' : IDL.Func([IDL.Principal], [], []),
    'isCallerAdmin' : IDL.Func([], [IDL.Bool], ['query']),
    'liquidateVault' : IDL.Func([IDL.Nat], [ProtocolResult_2], []),
    'liquidateVaultAmount' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [ProtocolResult_2],
        [],
      ),
    'mintTeamMmk' : IDL.Func([IDL.Principal, IDL.Nat], [ProtocolResult], []),
    'mintToken' : IDL.Func([Token, IDL.Float64], [ProtocolResult], []),
    'mintUSDM' : IDL.Func([IDL.Nat, IDL.Nat], [ProtocolResult], []),
    'onOraclePricesUpdated' : IDL.Func([], [], []),
    'previewLiquidation' : IDL.Func(
        [IDL.Nat],
        [IDL.Opt(LiquidationPreview)],
        ['query'],
      ),
    'quoteBuyMmkWithIcp' : IDL.Func([IDL.Nat], [IDL.Nat], ['query']),
    'quoteMmkSwap' : IDL.Func([IDL.Nat], [IDL.Opt(IDL.Nat)], ['query']),
    'quoteRedeemUsdmForIcp' : IDL.Func(
        [IDL.Nat],
        [
          IDL.Record({
            'usdmNetE8' : IDL.Nat,
            'icpOutE8' : IDL.Nat,
            'enabled' : IDL.Bool,
            'icpPriceE8' : IDL.Nat,
            'opsIcpAvailableE8' : IDL.Nat,
            'feeE8' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'quoteStabilitySwap' : IDL.Func(
        [StabilitySwapDirection, IDL.Nat],
        [StabilitySwapQuote],
        ['query'],
      ),
    'quoteSwapAssetForUsdm' : IDL.Func(
        [CollateralType, IDL.Nat],
        [
          IDL.Record({
            'usdmGrossE8' : IDL.Nat,
            'enabled' : IDL.Bool,
            'assetPriceE8' : IDL.Nat,
            'usdmOutE8' : IDL.Nat,
            'inventoryE8' : IDL.Nat,
            'feeE8' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'quoteSwapUsdmForAsset' : IDL.Func(
        [CollateralType, IDL.Nat],
        [
          IDL.Record({
            'usdmNetE8' : IDL.Nat,
            'enabled' : IDL.Bool,
            'assetPriceE8' : IDL.Nat,
            'inventoryE8' : IDL.Nat,
            'assetOutE8' : IDL.Nat,
            'feeE8' : IDL.Nat,
          }),
        ],
        ['query'],
      ),
    'recordAnalyticsSnapshot' : IDL.Func([], [], []),
    'redeemUsdmForIcp' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'refreshOraclePrice' : IDL.Func([CollateralType], [ProtocolResult_1], []),
    'registerDemoFaucet' : IDL.Func([IDL.Principal], [], []),
    'repayUSDM' : IDL.Func([IDL.Nat, IDL.Nat], [ProtocolResult], []),
    'runSurplusWaterfall' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'saveCallerUserProfile' : IDL.Func([UserProfile], [ProtocolResult], []),
    'setAdminPricePolicy' : IDL.Func(
        [IDL.Bool, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setAgentGateway' : IDL.Func([IDL.Principal], [], []),
    'setBalancedEngineParams' : IDL.Func(
        [IDL.Bool, IDL.Bool, IDL.Nat, IDL.Bool, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setCkUsdcLedger' : IDL.Func([IDL.Principal], [], []),
    'setCollateralLedger' : IDL.Func([CollateralType, IDL.Principal], [], []),
    'setCycleStationCmcEnabled' : IDL.Func([IDL.Bool], [ProtocolResult], []),
    'setCycleStationIcpLimits' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setCycleStationParams' : IDL.Func(
        [IDL.Bool, IDL.Nat, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setCycleStationTargets' : IDL.Func(
        [
          IDL.Opt(IDL.Principal),
          IDL.Opt(IDL.Principal),
          IDL.Opt(IDL.Principal),
        ],
        [ProtocolResult],
        [],
      ),
    'setGasDonationMessage' : IDL.Func(
        [IDL.Nat, IDL.Text],
        [ProtocolResult],
        [],
      ),
    'setGasFundingParams' : IDL.Func(
        [IDL.Nat, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setGlobalDebtCeiling' : IDL.Func([IDL.Nat], [], []),
    'setLedgerCanisters' : IDL.Func([IDL.Principal, IDL.Principal], [], []),
    'setLiquidationThresholdE8' : IDL.Func([IDL.Nat], [], []),
    'setLiquidityReserveBalances' : IDL.Func(
        [IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setMmkBookCanister' : IDL.Func([IDL.Principal], [ProtocolResult], []),
    'setMmkSwapParams' : IDL.Func(
        [IDL.Nat, IDL.Nat, IDL.Bool],
        [ProtocolResult],
        [],
      ),
    'setOracleAutoSync' : IDL.Func([IDL.Bool, IDL.Nat], [], []),
    'setOracleCanister' : IDL.Func([IDL.Principal], [], []),
    'setOracleMaxAgeHours' : IDL.Func([IDL.Nat], [], []),
    'setOsmDelayHours' : IDL.Func([IDL.Nat], [], []),
    'setParamQuorumBps' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'setPauseFlags' : IDL.Func([IDL.Bool, IDL.Bool, IDL.Bool], [], []),
    'setPhaseBParams' : IDL.Func([IDL.Bool, IDL.Nat], [ProtocolResult], []),
    'setPriceFeedImmediate' : IDL.Func(
        [CollateralType, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setProductionHardening' : IDL.Func([IDL.Bool], [], []),
    'setStabilitySwapParams' : IDL.Func(
        [IDL.Nat, IDL.Nat, IDL.Bool],
        [ProtocolResult],
        [],
      ),
    'setSurplusConfig' : IDL.Func(
        [IDL.Nat, IDL.Nat, IDL.Nat, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setSurplusWaterfallSplit' : IDL.Func(
        [IDL.Nat, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'setTreasuryIndex' : IDL.Func([IDL.Principal], [], []),
    'setUsdmIcpRedeemParams' : IDL.Func(
        [IDL.Nat, IDL.Bool],
        [ProtocolResult],
        [],
      ),
    'setWaterfallAuto' : IDL.Func(
        [IDL.Bool, IDL.Nat, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'settleBadDebtFromSurplus' : IDL.Func([], [ProtocolResult_1], []),
    'stakeMMK' : IDL.Func([IDL.Float64], [ProtocolResult], []),
    'stakeMMKE8' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'startLiquidationAuction' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'swapAssetForUsdm' : IDL.Func(
        [CollateralType, IDL.Nat],
        [ProtocolResult_1],
        [],
      ),
    'swapCkUsdcForUsdm' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'swapUsdmForAsset' : IDL.Func(
        [CollateralType, IDL.Nat],
        [ProtocolResult_1],
        [],
      ),
    'swapUsdmForCkUsdc' : IDL.Func([IDL.Nat], [ProtocolResult_1], []),
    'syncPriceFromOracle' : IDL.Func([CollateralType], [ProtocolResult_1], []),
    'tallyProposal' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'unstakeMMK' : IDL.Func([IDL.Float64], [ProtocolResult], []),
    'unstakeMMKE8' : IDL.Func([IDL.Nat], [ProtocolResult], []),
    'updatePriceFeed' : IDL.Func(
        [CollateralType, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'voteProposal' : IDL.Func([IDL.Nat, IDL.Bool], [ProtocolResult], []),
    'withdrawCollateral' : IDL.Func(
        [IDL.Nat, CollateralType, IDL.Nat],
        [ProtocolResult],
        [],
      ),
    'withdrawSavings' : IDL.Func([IDL.Float64], [ProtocolResult], []),
  });
};
export const init = ({ IDL }) => { return []; };